import { useEffect, useState } from 'react';
import { Loader2, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { getLanguageQuestionBank, updateLanguageQuestionBank } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';

export default function LanguageTestEditor() {
  const [code, setCode] = useState('');
  const [bank, setBank] = useState(null);
  const [index, setIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!code) return;
    let active = true;
    setLoading(true);
    setBank(null);
    setIndex(0);
    setError('');
    getLanguageQuestionBank(code)
      .then(({ data }) => { if (active) setBank(data); })
      .catch(() => { if (active) setError('Could not load the test. Close the editor and try again.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [code]);

  const question = bank?.questions[index];
  const change = (patch) => setBank((previous) => ({
    ...previous,
    questions: previous.questions.map((item, position) => position === index ? { ...item, ...patch } : item),
  }));
  const save = async () => {
    const invalid = bank.questions.findIndex((item) => !item.text.trim()
      || item.options.some((option) => !option.trim())
      || !Number.isInteger(item.answer) || item.answer < 0 || item.answer >= item.options.length);
    if (invalid >= 0) {
      setIndex(invalid);
      setError(`Complete question ${invalid + 1}, all answer choices, and its correct answer.`);
      return;
    }
    setSaving(true);
    setError('');
    try {
      await updateLanguageQuestionBank(code, { revision: bank.revision, questions: bank.questions });
      toast.success(`${code === 'english' ? 'English' : 'French'} test updated`);
      setCode('');
    } catch (failure) {
      const detail = failure?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Could not save the test. Check the question fields and try again.');
    } finally {
      setSaving(false);
    }
  };

  return <>
    <section className="rounded-xl border border-[rgba(6,43,26,0.10)] bg-white p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-semibold text-[#062B1A]">Test questions</h2>
          <p className="mt-1 text-sm text-[#062B1A]/60">Edit questions and correct answers for future invitations.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {['english', 'french'].map((language) => <Button key={language} variant="outline" onClick={() => setCode(language)}>
            <Pencil className="mr-2 size-4" />Edit {language === 'english' ? 'English' : 'French'} test
          </Button>)}
        </div>
      </div>
    </section>
    <Dialog open={Boolean(code)} onOpenChange={(open) => { if (!open && !saving) setCode(''); }}>
      <DialogContent className="flex max-h-[90vh] flex-col sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>Edit {code === 'english' ? 'English' : 'French'} test</DialogTitle>
          <DialogDescription>Changes apply to newly sent tests. Existing invitations and results keep their original questions. Question order and scoring stay the same.</DialogDescription>
        </DialogHeader>
        {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
        {loading && <p role="status" className="py-8 text-center">Loading questions…</p>}
        {question && <fieldset disabled={saving} className="min-h-0 space-y-4 overflow-y-auto px-1 pb-2">
          <div className="space-y-1">
            <Label htmlFor="test-question-select">Question</Label>
            <select id="test-question-select" className="h-10 w-full rounded-md border bg-white px-3 text-sm" value={index} onChange={(event) => setIndex(Number(event.target.value))}>
              {bank.questions.map((item, position) => <option key={item.id} value={position}>{position + 1}. {item.text.slice(0, 85)}</option>)}
            </select>
            <p className="text-xs text-[#062B1A]/60">Question {index + 1} of {bank.questions.length}{question.skill ? ` · ${question.skill}` : ''}{question.level ? ` · ${question.level}` : ''}</p>
          </div>
          <div className="space-y-1">
            <Label htmlFor="test-question-text">Question text</Label>
            <Textarea id="test-question-text" rows={3} maxLength={5000} value={question.text} onChange={(event) => change({ text: event.target.value })} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="test-question-passage">Reading passage (optional)</Label>
            <Textarea id="test-question-passage" rows={3} maxLength={15000} value={question.passage || ''} onChange={(event) => change({ passage: event.target.value })} />
          </div>
          <fieldset className="space-y-2">
            <legend className="mb-2 text-sm font-medium">Answer choices — select the correct answer</legend>
            {question.options.map((option, position) => <div key={position} className="flex items-center gap-3">
              <input type="radio" name="correct-answer" aria-label={`Mark option ${position + 1} correct`} checked={question.answer === position} onChange={() => change({ answer: position })} />
              <Input aria-label={`Option ${position + 1}`} maxLength={2000} value={option} onChange={(event) => change({ options: question.options.map((value, i) => i === position ? event.target.value : value) })} />
            </div>)}
          </fieldset>
          <div className="flex justify-between">
            <Button variant="outline" disabled={index === 0} onClick={() => setIndex(index - 1)}>Previous question</Button>
            <Button variant="outline" disabled={index === bank.questions.length - 1} onClick={() => setIndex(index + 1)}>Next question</Button>
          </div>
        </fieldset>}
        <DialogFooter>
          <Button variant="outline" disabled={saving} onClick={() => setCode('')}>Cancel</Button>
          <Button disabled={!bank || loading || saving} onClick={save} className="bg-[#062B1A] text-white hover:bg-[#041B10]">
            {saving && <Loader2 className="mr-2 size-4 animate-spin" />}Save test
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </>;
}
